/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable max-nested-callbacks */
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Skeleton,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  AccordionDetails,
  Accordion,
  Autocomplete,
  TextField,
  InputLabel,
  AccordionSummary,
  Radio,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { getDecryptAns } from '../../../../../utils/decryptAns';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import { useProgramCreation } from '../../ProgramCreationContext';
import { API } from '../../../../../api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getUserDetails } from '../../../../../utils/orgName';
import {
  Lesson,
  ModuleDraft,
  createLesson,
  createModule,
  LessonMaterial,
  FeedbackQuestion,
  FeedbackQuestionType,
  mapFeedbackTypeToAnswerFormat,
  mapAnswerFormatToFeedbackType,
  getFeedbackTypeLabel
} from '../types';
import ContentsModal from '../../../../../pages/new-designs/tabs/Program/components/program tabs/settings components/ContentsModal';
import CommonModalHeader from '../../../../../components/DialogUi/CommonModalHeader';
import AddEditContent from '../../../../../pages/new-designs/tabs/Help/components/AddEditContent';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { fetchAllArticles } from '../../../../../redux/slices/article/getAllArticleSlice';
import { fetchGroupDetails, grpDetailsActions } from '../../../../../redux/slices/group-details/groupDetails';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import StepNavigator from '../components/StepNavigator';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import { GenericAccordion } from '../../../../../pages/new-designs/tabs/Program/components/program tabs/ProgramSettings';
import DosenAutocomplete from '../../../../../pages/new-designs/components/DosenAutocomplete';

const inputSx = {
  '& .MuiInputBase-root': {
    background: 'rgba(0,0,0,0.7) !important',
    borderRadius: '6px',
    color: '#FDFDFD'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#868686',
    color: '#FDFDFD'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#B4B4B4'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#F79420'
  },
  '& .MuiInputLabel-root': {
    color: '#B6B6B6',
    background: 'rgba(0,0,0,0.7) !important'
  }
};

interface ManualProgramBuilderProps {
  showLessonEditor?: boolean;
  hideSaveButton?: boolean;
  registerSaveHandler?: (handler: (() => Promise<boolean>) | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  skipTopicIndustryUpdate?: boolean;
  onProgramNameChange?: (value: string) => void;
  model?: string;
  useUpdateGroupTasks?: boolean;
  updateTemplateId?: string;
}

export const ManualProgramBuilder = ({
  showLessonEditor = true,
  hideSaveButton = false,
  registerSaveHandler,
  onDirtyChange,
  skipTopicIndustryUpdate = false,
  onProgramNameChange,
  model,
  useUpdateGroupTasks = false,
  updateTemplateId
}: ManualProgramBuilderProps) => {
  const { state, dispatch } = useProgramCreation();
  const initialModules = (state.modules as ModuleDraft[] | undefined) ?? [];
  const [modules, setModules] = useState<ModuleDraft[]>(initialModules);
  const [programName, setProgramName] = useState(state.masterclass?.title || '');
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(modules[0]?.id || null);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(modules[0]?.lessons[0]?.id || null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSavedBeforeNext, setHasSavedBeforeNext] = useState(false);
  const hydrationBlockedRef = useRef(false);
  const handleSaveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false));
  const setDirtyState = useCallback(
    (value: boolean) => {
      setDirty(value);
      onDirtyChange?.(value);
      if (value) {
        setHasSavedBeforeNext(false);
      }
    },
    [onDirtyChange]
  );
  const [draggingModule, setDraggingModule] = useState<string | null>(null);
  const [draggingLesson, setDraggingLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [moduleMenuAnchor, setModuleMenuAnchor] = useState<null | HTMLElement>(null);
  const [moduleMenuId, setModuleMenuId] = useState<string | null>(null);
  const [lessonMenuAnchor, setLessonMenuAnchor] = useState<null | HTMLElement>(null);
  const [lessonMenuMeta, setLessonMenuMeta] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [resourceChoiceLesson, setResourceChoiceLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [articleDetailsCache, setArticleDetailsCache] = useState<Record<string, any>>({});
  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [lessonDraft, setLessonDraft] = useState<{ title: string; description: string }>({
    title: '',
    description: ''
  });
  const [feedbackExpanded, setFeedbackExpanded] = useState(true);
  const [mcqExpanded, setMcqExpanded] = useState(true);
  const [addFeedbackModalOpen, setAddFeedbackModalOpen] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState<{ prompt: string; type: FeedbackQuestionType }>({
    prompt: '',
    type: 'text'
  });
  const [removeFeedbackId, setRemoveFeedbackId] = useState<string | null>(null);
  const [removeMcqId, setRemoveMcqId] = useState<string | null>(null);
  const [draggingOption, setDraggingOption] = useState<{ mcqId: string; optionId: string } | null>(null);
  const [addEditModalMeta, setAddEditModalMeta] = useState<{
    open: boolean;
    action: 'Add' | 'Edit';
    articleId?: string;
    moduleId?: string;
    lessonId?: string;
    materialId?: string;
    material?: LessonMaterial;
    articleData?: any;
  }>({ open: false, action: 'Add' });
  const [lessonRenameMeta, setLessonRenameMeta] = useState<{
    moduleId: string;
    lessonId: string;
    title: string;
  } | null>(null);
  const { id: grpId = '', coId: coIdParam, orgId: orgIdParam } = useParams();
  const { location } = getUserDetails();
  const reduxDispatch = useAppDispatch();
  const refreshGroupDetails = useCallback(
    async (groupId: string) => {
      if (!groupId) return;
      await reduxDispatch(
        fetchGroupDetails({
          grpId: groupId,
          loc: location,
          useCache: false
        })
      );
    },
    [location, reduxDispatch]
  );
  const orgIdParamNew = useAppSelector((state) => state.orgDetails.data?._id) || '';
  const parentIdParamNew = useAppSelector((state) => state.orgDetails.data?.grpId) || '';
  const articleState = useAppSelector((state) => state.allArticle);
  const articleList = articleState.data?.ArticleList || [];
  const articleMap = useMemo(
    () =>
      articleList.reduce<Record<string, any>>((acc, article) => {
        acc[article.articleId] = article;
        return acc;
      }, {}),
    [articleList]
  );
  const hydrateMaterials = useCallback(
    (mods: ModuleDraft[]) =>
      mods.map((module) => ({
        ...module,
        lessons: module.lessons.map((lesson) => {
          const rawMaterials = lesson.materials && lesson.materials.length > 0 ? lesson.materials : [];
          const legacyArticles = (lesson as any).libraryArticles || [];
          const mergedMaterials: LessonMaterial[] = [
            ...rawMaterials,
            ...legacyArticles
              .filter((id: string) => !rawMaterials.some((mat) => (mat.articleId || mat.id) === id))
              .map((articleId: string) => ({
                id: articleId,
                articleId,
                title: '',
                reference: '',
                source: 'library' as const
              }))
          ];

          const hydrated = mergedMaterials.map((material) => {
            const articleId = material.articleId || material.id;
            const article = articleId ? articleMap[articleId] : null;
            const source = material.source || (article ? 'library' : 'custom');
            if (source !== 'library') return material;
            if (!article) {
              return {
                ...material,
                id: material.id || articleId || nanoid(),
                articleId: articleId || material.articleId || '',
                title: material.title || 'Resource',
                thumbnail: material.thumbnail,
                reference: material.reference || '',
                readTime: material.readTime
              };
            }
            return {
              ...material,
              id: material.id || article.articleId,
              articleId: article.articleId,
              title: material.title || article.articleName || 'Resource',
              thumbnail: material.thumbnail || article.coverImageUrl,
              readTime: material.readTime || article.readTime,
              reference: material.reference || article.articleUrl || article.articleLink || article.fileUrl || ''
            };
          });

          return { ...lesson, materials: hydrated };
        })
      })),
    [articleMap]
  );
  const hydrateFromGoalLevels = useCallback((): ModuleDraft[] => {
    const goalLevels: any[] = (state.masterclass as any)?.levels || [];
    if (!goalLevels.length) return [];
    return goalLevels.map((level, levelIdx) => {
      const lessons =
        level.tasks?.map((task: any) => {
          const feedbackQuestions: FeedbackQuestion[] =
            task.menteeQuestions
              ?.map((q: any) => ({
                id: q._id || nanoid(),
                _id: q._id,
                prompt: q.menteeQues || '',
                type: mapAnswerFormatToFeedbackType(q?.menteeAnsType || q?.type)
              }))
              .filter(Boolean) || [];

          const mcqs =
            task.MCQ?.map((mcq: any) => {
              const optionStrings = mcq.options || [];
              const options = optionStrings.map((opt: string) => ({ id: nanoid(), label: opt }));
            const decrypted = mcq.correctAns ? getDecryptAns(mcq.correctAns) : mcq.correctAns;
            const correctOptionId = options.find((opt: any) => opt.label === decrypted)?.id || options[0]?.id || '';
            return {
              id: mcq._id || nanoid(),
              _id: mcq._id,
              prompt: mcq.ques || '',
              options: options,
              correctOptionId
            };
            }) || [];

          const materials =
            task.libraryArticles?.map((articleId: string) => ({
              id: articleId || nanoid(),
              articleId,
              title: '',
              reference: '',
              source: 'library' as const
            })) || [];

          return {
            id: task._id || nanoid(),
            title: task.taskName || `Lesson ${levelIdx + 1}`,
            description: task.taskDescription || '',
            materials,
            feedbackQuestions,
            multipleChoiceQuestions: mcqs
          } as Lesson;
        }) || [];

      return {
        id: level._id || nanoid(),
        title: level.levelName || `Module ${levelIdx + 1}`,
        objective: level.levelDescription || '',
        lessons: lessons.length ? lessons : [createLesson()]
      };
    });
  }, [state.masterclass]);
  const isAIMode = state.buildMode === 'ai';
  const isManualFlow = state.buildMode === 'manual';
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const showManualFooter = !isAIMode && routerLocation.pathname.includes('/manual');
  const isMongoId = (val?: string) => Boolean(val && /^[a-fA-F0-9]{24}$/.test(val));
  const [infoVisible, setInfoVisible] = useState(true);
  const [materialsModalLesson, setMaterialsModalLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);

  const articleListLength = articleList.length;

  const userRoleOverride = localStorage.getItem('role') || 'mentor';
  const routeGroupId = grpId && grpId !== 'admin' ? grpId : coIdParam;
  const groupIdForArticles = state.groupId || routeGroupId;
  const orgIdParamArticlesManual = orgIdParam || '';
  const orgIdUsedForArticles = isManualFlow
    ? orgIdParamArticlesManual || orgIdParamNew
    : orgIdParamNew || orgIdParamArticlesManual;

  const isContentGenerating = state.ai.contentLoading || state.ai.contentPollingActive;
  const contentProgressValue = state.ai.contentProgress?.percentage ?? null;
  const shouldDisablePendingActions = dirty && !saving && !(isAIMode && isContentGenerating);
  const isNextButtonDisabled =
    !state.groupId || saving || shouldDisablePendingActions || !hasSavedBeforeNext;
  useEffect(() => {
    console.log('[ManualProgramBuilder] AI generator state', {
      isAIMode,
      isContentGenerating,
      contentProgressValue
    });
  }, [isAIMode, isContentGenerating, contentProgressValue]);
  const feedbackAnswerOptions = useMemo(
    () => [
      { label: 'Text', value: 'Open input field', type: 'text' as FeedbackQuestionType },
      { label: '1-5 Rating', value: '1-5 rating', type: 'rating' as FeedbackQuestionType },
      { label: 'Yes or No', value: 'Yes, No', type: 'yesno' as FeedbackQuestionType },
      { label: 'List of Industries', value: 'List of industries', type: 'list' as FeedbackQuestionType },
      { label: 'List of Roles', value: 'List of roles', type: 'roles' as FeedbackQuestionType }
    ],
    []
  );

  const activeModule = useMemo(
    () => modules.find((m) => m.id === selectedModuleId) || null,
    [modules, selectedModuleId]
  );
  const activeLesson = useMemo(
    () => activeModule?.lessons.find((l) => l.id === selectedLessonId) || null,
    [activeModule, selectedLessonId]
  );

  useEffect(() => {
    if (activeLesson) {
      setLessonDraft({ title: activeLesson.title, description: activeLesson.description });
      setIsEditingLesson(false);
    }
  }, [activeLesson]);

  const handleAddFeedbackQuestion = () => {
    setFeedbackDraft({ prompt: '', type: 'text' });
    setAddFeedbackModalOpen(true);
  };

  const handleSaveNewFeedback = () => {
    if (!activeModule || !activeLesson) return;
    if (!feedbackDraft.prompt.trim()) return;
    const newQuestion: FeedbackQuestion = {
      id: nanoid(),
      prompt: feedbackDraft.prompt.trim(),
      type: feedbackDraft.type
    };
    updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
      ...lesson,
      feedbackQuestions: [...lesson.feedbackQuestions, newQuestion]
    }));
    setAddFeedbackModalOpen(false);
  };

  const handleConfirmRemoveFeedback = () => {
    if (!activeModule || !activeLesson || !removeFeedbackId) return;
    updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
      ...lesson,
      feedbackQuestions: lesson.feedbackQuestions.filter((item) => item.id !== removeFeedbackId)
    }));
    setRemoveFeedbackId(null);
  };

  useEffect(() => {
    if (hydrationBlockedRef.current || (dirty && !isAIMode)) {
      return;
    }
    const sourceModules = (state.modules as ModuleDraft[] | undefined) ?? [];
    const hasDraftShape =
      sourceModules.length > 0 && sourceModules.every((m: any) => Array.isArray(m?.lessons) && m.lessons.length >= 0);
    const hydratedExisting = hasDraftShape ? hydrateMaterials(sourceModules) : [];
    const fallbackFromGoal = hydrateFromGoalLevels();
    const nextModules = hydratedExisting.length ? hydratedExisting : hydrateMaterials(fallbackFromGoal);
    const serializedCurrent = JSON.stringify(modules);
    const serializedNext = JSON.stringify(nextModules);
    if (serializedCurrent !== serializedNext) {
      setModules(nextModules);
    }

    setSelectedModuleId((prevModuleId) => {
      if (!nextModules.length) {
        setSelectedLessonId(null);
        return null;
      }

      if (!prevModuleId || !nextModules.some((module) => module.id === prevModuleId)) {
        const firstModule = nextModules[0];
        const firstLessonId = firstModule.lessons[0]?.id || null;
        setSelectedLessonId(firstLessonId);
        return firstModule.id;
      }

      const currentModule = nextModules.find((module) => module.id === prevModuleId);
      if (currentModule) {
        setSelectedLessonId((prevLessonId) => {
          if (!prevLessonId || !currentModule.lessons.some((lesson) => lesson.id === prevLessonId)) {
            return currentModule.lessons[0]?.id || null;
          }
          return prevLessonId;
        });
      }

      return prevModuleId;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.modules, state.masterclass, hydrateMaterials, hydrateFromGoalLevels, dirty]);

  useEffect(() => {
    if (
      lessonRenameMeta &&
      !modules.some((module) => module.lessons.some((lesson) => lesson.id === lessonRenameMeta.lessonId))
    ) {
      setLessonRenameMeta(null);
    }
  }, [modules, lessonRenameMeta]);

  useEffect(() => {
    const enriched = hydrateMaterials(modules);
    if (JSON.stringify(enriched) !== JSON.stringify(modules)) {
      setModules(enriched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleMap, hydrateMaterials]);

  const persistModules = useCallback(
    (updater: (prev: ModuleDraft[]) => ModuleDraft[]) => {
      setModules((prev) => {
        const next = updater(prev);
        dispatch({ type: 'SET_MODULES', payload: next });
        setDirtyState(true);
        return next;
      });
    },
    [dispatch, setDirtyState]
  );

  const handleProgramNameChange = (value: string) => {
    setProgramName(value);
    setDirtyState(true);
    dispatch({ type: 'UPDATE_MASTERCLASS', payload: { title: value } });
    onProgramNameChange?.(value);
  };

  const handleAddModule = () => {
    persistModules((prev) => {
      const next = [...prev, createModule(prev.length + 1)];
      const newlyAdded = next[next.length - 1];
      setSelectedModuleId(newlyAdded.id);
      setSelectedLessonId(newlyAdded.lessons[0].id);
      return next;
    });
  };

  const handleAddLesson = (moduleId: string) => {
    persistModules((prev) =>
      prev.map((module) =>
        module.id === moduleId ? { ...module, lessons: [...module.lessons, createLesson()] } : module
      )
    );
  };

  const updateModule = (moduleId: string, updater: (module: ModuleDraft) => ModuleDraft) => {
    persistModules((prev) => prev.map((module) => (module.id === moduleId ? updater(module) : module)));
  };

  const updateLesson = (moduleId: string, lessonId: string, updater: (lesson: Lesson) => Lesson) => {
    updateModule(moduleId, (module) => ({
      ...module,
      lessons: module.lessons.map((lesson) => (lesson.id === lessonId ? updater(lesson) : lesson))
    }));
  };

  const removeModule = (moduleId: string) => {
    persistModules((prev) => {
      const filtered = prev.filter((module) => module.id !== moduleId);
      if (filtered.length === 0) {
        setSelectedModuleId(null);
        setSelectedLessonId(null);
      } else if (selectedModuleId === moduleId) {
        const nextModule = filtered[0];
        setSelectedModuleId(nextModule.id);
        setSelectedLessonId(nextModule.lessons[0]?.id || null);
      }
      return filtered;
    });
  };

  const removeLesson = (moduleId: string, lessonId: string) => {
    updateModule(moduleId, (module) => {
      const updatedLessons = module.lessons.filter((lesson) => lesson.id !== lessonId);
      if (updatedLessons.length === 0) {
        updatedLessons.push(createLesson());
      }
      if (module.id === selectedModuleId && lessonId === selectedLessonId) {
        setSelectedLessonId(updatedLessons[0].id);
      }
      return { ...module, lessons: updatedLessons };
    });
  };

  const buildLevelsPayload = useCallback(
    (sourceModules: ModuleDraft[]) =>
      sourceModules.map((module, moduleIndex) => {
        const modulePayload: Record<string, any> = {
          levelName: module.title || `Module ${moduleIndex + 1}`,
          levelDescription: module.objective || '',
          level: moduleIndex + 1,
          CheckInWithMentor: 'no',
          startDate: { type: 'activeNow', value: 0 },
          endDate: { type: 'activeNow', value: 0 }
        };

        if (module.sourceId) {
          modulePayload.id = module.sourceId;
        }

        modulePayload.tasks = module.lessons.map((lesson, lessonIndex) => {
          const lessonPayload: Record<string, any> = {
            taskName: lesson.title || `Lesson ${lessonIndex + 1}`,
            taskDescription: lesson.description || 'Lesson description',
            taskPosition: lessonIndex + 1,
            MCQ:
              lesson.multipleChoiceQuestions.length > 0
                ? lesson.multipleChoiceQuestions.map((mcq, mcqIdx) => {
                    const baseOptions = mcq.options.length > 0 ? mcq.options : [{ id: nanoid(), label: 'Option 1' }];
                    const paddedOptions =
                      baseOptions.length >= 4
                        ? baseOptions
                        : [
                            ...baseOptions,
                            ...Array.from({ length: 4 - baseOptions.length }).map((_, idx) => ({
                              id: nanoid(),
                              label: `Option ${baseOptions.length + idx + 1}`
                            }))
                          ];
                    const optionTexts = paddedOptions.map((opt, idx) => opt.label || `Option ${idx + 1}`);
                    const correctIndex =
                      mcq.correctOptionId && paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId) !== -1
                        ? paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId)
                        : 0;

                    const mcqPayload: Record<string, any> = {
                      ques: mcq.prompt || `MCQ ${mcqIdx + 1}`,
                      options: optionTexts.map((text, idx) => ({
                        optionText: text,
                        ans: idx === correctIndex
                      }))
                    };

                    if (mcq._id) {
                      mcqPayload._id = mcq._id;
                    }

                    return mcqPayload;
                  })
                : [],
            menteeQuestions:
              lesson.feedbackQuestions.length > 0
                ? lesson.feedbackQuestions.map((question, idx) => {
                    const questionPayload: Record<string, any> = {
                      menteeQues: question.prompt || `Question ${idx + 1}`,
                      menteeAnsType: mapFeedbackTypeToAnswerFormat(question.type)
                    };
                    if (question._id) {
                      questionPayload._id = question._id;
                    }
                    return questionPayload;
                  })
                : [],
            menteeAsmtQues: 'Have you completed this lesson?',
            menteeAsmtAnsType: 'Yes, No',
            menteeAsmtAnsOptions: ['Yes', 'No'],
            libraryArticles: lesson.materials
              .filter((material) => ['library', 'ai'].includes((material.source || 'custom') as string))
              .map((material) => material.articleId || material.id)
              .filter(Boolean),
            startDate: 0,
            endDate: 0
          };

          if (lesson.sourceId) {
            lessonPayload.id = lesson.sourceId;
          }

          return lessonPayload;
        });

        return modulePayload;
      }),
    []
  );

  const handleSave = async (): Promise<boolean> => {
    if (isEditingLesson && activeModule && activeLesson) {
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        title: lessonDraft.title,
        description: lessonDraft.description
      }));
      setIsEditingLesson(false);
    }

    if (!programName.trim()) {
      toast.error('Program name is required');
      return false;
    }
    if (!modules.length) {
      toast.error('Add at least one module before saving');
      return false;
    }

    hydrationBlockedRef.current = true;
    setSaving(true);
    try {
      let orgForCreate = (orgIdParamNew as string) || '';
      const parentGroupId = (parentIdParamNew as string) || '';

      if (!orgForCreate || !parentGroupId) {
        toast.error('Missing program context');
        return false;
      }

      const safeGoalName = (programName.trim() || 'Program').replace(/[^a-zA-Z0-9\s-_]/g, '') || 'Program';
      const levels = buildLevelsPayload(modules) as any;
      const updateGroupGoals = (goals: any) => {
        if (!goals) return;
        reduxDispatch(
          grpDetailsActions.updateGroupGoalInfo({
            programType: goals.programType || 'Custom',
            goalName: goals.goalName || safeGoalName,
            templateId: goals.templateId || '',
            programKPIS: goals.programKPIS || ''
          })
        );
      };

      const payload = { goalName: safeGoalName, programType: 'Custom', levels };
      const performTaskSave = (groupId: string) => {
        if (useUpdateGroupTasks && updateTemplateId) {
          return API.updateGroupTasks({
            data: payload,
            grpId: groupId,
            location,
            orgId: orgForCreate,
            templateId: updateTemplateId
          });
        }

        return API.saveGroupTasks({
          data: payload,
          grpId: groupId,
          location,
          orgId: orgForCreate
        });
      };

      const updateModelInfo = async (groupId: string) => {
        if (!model) return;
        const formData = new FormData();
        formData.append('model', model);
        formData.append('mentee', 'mentee');
        formData.append('mentor', 'mentor');

        await API.updateGroupInfo({
          orgId: orgForCreate,
          grpId: groupId,
          formData
        });
      };

      if (isAIMode) {
        if (!state.groupId) {
          toast.error('Missing AI workspace. Please restart the AI flow.');
          return false;
        }

        const response = await performTaskSave(state.groupId);

        updateGroupGoals(response.data?.goals);
        await updateModelInfo(state.groupId);
        await API.updateGroupSettings({ groupId: state.groupId, location });
        await refreshGroupDetails(state.groupId);
        toast.success('Draft saved');
        // setDirtyState(false);
        // return true;
      }

      let targetGroupId = state.groupId;

      if (!targetGroupId) {
        const createRes = await API.createGroup({
          orgId: orgForCreate,
          parent: parentGroupId,
          name: programName,
          location
        });

        targetGroupId =
          createRes?.data?.data?._id ||
          createRes?.data?.data?.grpId ||
          createRes?.data?.groupDetails?.grpId ||
          createRes?.data?.grpId ||
          createRes?.data?.groupId ||
          createRes?.data?.id;

        if (!targetGroupId) {
          toast.error('Failed to create program');
          return false;
        }

        dispatch({ type: 'SET_GROUP_ID', payload: targetGroupId });
      }

      const res = await performTaskSave(targetGroupId);
      toast.success('Program saved');

      if (!skipTopicIndustryUpdate) {
        await Promise.all([
          API.updateTopicsAndIndustry({
            grpId: targetGroupId,
            orgId: orgForCreate,
            data: { topics: ['Topic Creation'] },
            key: 'topics'
          }),
          API.updateTopicsAndIndustry({
            grpId: targetGroupId,
            orgId: orgForCreate,
            data: { industries: ['Industry Creation'] },
            key: 'industries'
          })
        ]);
      }

      updateGroupGoals(res.data?.goals);

      await updateModelInfo(targetGroupId);
      await API.updateGroupSettings({ groupId: targetGroupId, location });
      await refreshGroupDetails(targetGroupId);

      if (state.programType !== 'mentorship') {
        const publishResp = await API.publishTask({ location, grpId: targetGroupId, orgId: orgForCreate });
        if (publishResp?.status === 200) {
          reduxDispatch(grpDetailsActions.updatePublishKey(true));
          toast.success('Program published successfully');
        } else {
          toast.error('Program failed to publish');
        }
      }

      setDirtyState(false);
      setHasSavedBeforeNext(true);
      return true;
    } catch (error) {
      toast.error('Failed to save program');
      return false;
    } finally {
      setSaving(false);
      hydrationBlockedRef.current = false;
    }
  };

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!registerSaveHandler) return undefined;
    registerSaveHandler(() => handleSaveRef.current());
    return () => registerSaveHandler(null);
  }, [registerSaveHandler]);

  const openModuleMenu = (event: React.MouseEvent<HTMLButtonElement>, moduleId: string) => {
    event.stopPropagation();
    setModuleMenuAnchor(event.currentTarget);
    setModuleMenuId(moduleId);
  };

  const closeModuleMenu = () => {
    setModuleMenuAnchor(null);
    setModuleMenuId(null);
  };

  const openLessonMenu = (event: React.MouseEvent<HTMLElement>, meta: { moduleId: string; lessonId: string }) => {
    event.stopPropagation();
    setLessonMenuAnchor(event.currentTarget);
    setLessonMenuMeta(meta);
  };

  const closeLessonMenu = () => {
    setLessonMenuAnchor(null);
    setLessonMenuMeta(null);
  };

  const beginRenameLesson = (moduleId: string, lessonId: string, currentTitle: string) => {
    setLessonRenameMeta({ moduleId, lessonId, title: currentTitle || '' });
    closeLessonMenu();
  };

  const finishLessonRename = () => {
    if (!lessonRenameMeta) return;
    const { moduleId, lessonId, title } = lessonRenameMeta;
    updateLesson(moduleId, lessonId, (lesson) => ({
      ...lesson,
      title: title.trim() || lesson.title
    }));
    setLessonRenameMeta(null);
  };

  const cancelLessonRename = () => {
    setLessonRenameMeta(null);
  };

  const beginRenameModule = (moduleId: string) => {
    setEditingModuleId(moduleId);
    closeModuleMenu();
  };

  const handleModuleDragDrop = (targetId: string | null) => {
    if (!draggingModule) return;
    persistModules((prev) => {
      const fromIndex = prev.findIndex((m) => m.id === draggingModule);
      if (fromIndex === -1) return prev;
      const toIndex = targetId ? prev.findIndex((m) => m.id === targetId) : prev.length - 1;
      if (toIndex === -1 || toIndex === fromIndex) return prev;
      const reordered = [...prev];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      return reordered;
    });
    setDraggingModule(null);
  };

  const handleLessonDrop = (moduleId: string, targetLessonId: string | null) => {
    if (!draggingLesson || draggingLesson.moduleId !== moduleId) return;
    updateModule(moduleId, (module) => {
      const lessons = [...module.lessons];
      const fromIndex = lessons.findIndex((lesson) => lesson.id === draggingLesson.lessonId);
      if (fromIndex === -1) return module;
      const toIndex = targetLessonId ? lessons.findIndex((lesson) => lesson.id === targetLessonId) : lessons.length;
      if (toIndex === -1 || fromIndex === toIndex) return module;
      const [moved] = lessons.splice(fromIndex, 1);
      lessons.splice(toIndex, 0, moved);
      return { ...module, lessons };
    });
    setDraggingLesson(null);
  };

  const currentMaterialsSelection = useMemo(() => {
    if (!materialsModalLesson) return [] as string[];
    const module = modules.find((m) => m.id === materialsModalLesson.moduleId);
    const lesson = module?.lessons.find((l) => l.id === materialsModalLesson.lessonId);
    if (!lesson) return [] as string[];
    return lesson.materials.map((material) => material.articleId).filter(Boolean) as string[];
  }, [materialsModalLesson, modules]);

  const fetchArticles = useCallback(
    (options?: { force?: boolean }) => {
      const targetGroupId = parentIdParamNew;
      const headerOrgId = coIdParam;
      if (!headerOrgId || !targetGroupId) return;
      if (!options?.force && (articleState.loading || articleListLength > 0)) return;
      reduxDispatch(
        fetchAllArticles({
          orgId: orgIdParam ?? '',
          groupId: state.groupId ?? '',
          helpSettings: false,
          useCache: false,
          role: 'admin'
        })
      );
    },
    [
      groupIdForArticles,
      orgIdParam,
      coIdParam,
      orgIdUsedForArticles,
      articleState.loading,
      articleListLength,
      reduxDispatch,
      userRoleOverride
    ]
  );

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMaterialSelection = (articleIds: string[]) => {
    if (!materialsModalLesson) return;
    const selectedMaterials = articleIds
      .map((articleId) => {
        const article = articleList.find((item) => item.articleId === articleId);
        if (!article) return null;
        const reference = ((article as any).articleUrl ||
          (article as any).articleLink ||
          article.fileUrl ||
          '') as string;
        const title = (article as any).articleName || (article as any).title || (article as any).name || 'Resource';
        const thumbnail = (article as any).coverImageUrl || (article as any).coverImg || '';
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
          id: articleId,
          articleId,
          title,
          reference,
          thumbnail,
          readTime: article.readTime,
          source: 'library'
        } as LessonMaterial;
      })
      .filter(Boolean) as LessonMaterial[];

    updateLesson(materialsModalLesson.moduleId, materialsModalLesson.lessonId, (lesson) => ({
      ...lesson,
      materials: [
        ...lesson.materials.filter((material) => (material.source || 'custom') !== 'library'),
        ...selectedMaterials
      ]
    }));
    setMaterialsModalLesson(null);
  };

  const closeResourceChoice = () => setResourceChoiceLesson(null);

  const handleSelectExistingResource = () => {
    if (!resourceChoiceLesson) return;
    fetchArticles({ force: true });
    setMaterialsModalLesson(resourceChoiceLesson);
    closeResourceChoice();
  };

  const handleCreateNewResource = () => {
    if (!resourceChoiceLesson) return;
    setAddEditModalMeta({
      open: true,
      action: 'Add',
      moduleId: resourceChoiceLesson.moduleId,
      lessonId: resourceChoiceLesson.lessonId,
      materialId: undefined,
      material: undefined
    });
    closeResourceChoice();
  };

  const loadArticleDetails = async (articleId?: string) => {
    if (!articleId) return null;
    if (articleDetailsCache[articleId]) {
      return articleDetailsCache[articleId];
    }
    try {
      const response = await API.getAllArticleById({
        orgId: orgIdParam ?? '',
        articleId,
        groupId: state.groupId ?? undefined,
        isContentSettings: true,
        articleLocation: location || 'us',
        useCache: false
      });
      const articleData = response?.data?.article;
      if (articleData) {
        setArticleDetailsCache((prev) => ({ ...prev, [articleId]: articleData }));
      }
      return articleData;
    } catch (error) {
      console.error('Failed to load article details', error);
      return null;
    }
  };

  const openMaterialEditor = async (material: LessonMaterial) => {
    if (!activeModule || !activeLesson) return;
    const articleId = material.articleId || material.id;
    let articleData = articleId ? articleMap[articleId] : undefined;
    if (!articleData && articleId) {
      articleData = await loadArticleDetails(articleId);
    }
    setAddEditModalMeta({
      open: true,
      action: 'Edit',
      articleId,
      moduleId: activeModule.id,
      lessonId: activeLesson.id,
      materialId: material.id,
      material,
      articleData
    });
  };

  const handleCloseAddEditModal = () => {
    setAddEditModalMeta((prev) => ({
      ...prev,
      open: false,
      articleId: undefined,
      materialId: undefined,
      material: undefined,
      articleData: undefined
    }));
  };

  const handleNewArticleCreated = (article: any) => {
    setAddEditModalMeta((prev) => ({
      ...prev,
      open: false,
      materialId: undefined,
      material: undefined,
      articleData: undefined
    }));
    const newArticle = Array.isArray(article) ? article[0] : article;
    const moduleId = addEditModalMeta.moduleId || activeModule?.id;
    const lessonId = addEditModalMeta.lessonId || activeLesson?.id;
    if (!newArticle || !moduleId || !lessonId) return;
    const articleId = newArticle.articleId || newArticle._id || nanoid();
    const material: LessonMaterial = {
      id: articleId,
      articleId: articleId,
      title: newArticle.articleName || newArticle.title || newArticle.name || 'Resource',
      reference: newArticle.articleUrl || newArticle.articleLink || newArticle.fileUrl || '',
      thumbnail: newArticle.coverImageUrl || newArticle.coverImg || '',
      readTime: newArticle.readTime,
      source: 'library'
    };
    if (addEditModalMeta.action === 'Edit' && addEditModalMeta.materialId) {
      updateLesson(moduleId, lessonId, (lesson) => ({
        ...lesson,
        materials: lesson.materials.map((item) =>
          item.id === addEditModalMeta.materialId
            ? {
                ...item,
                title: material.title,
                reference: material.reference,
                thumbnail: material.thumbnail,
                readTime: material.readTime,
                articleId: material.articleId,
                source: item.source || material.source
              }
            : item
        )
      }));
      return;
    }

    updateLesson(moduleId, lessonId, (lesson) => {
      const existing = lesson.materials.filter((item) => (item.articleId || item.id) !== material.articleId);
      return {
        ...lesson,
        materials: [...existing, material]
      };
    });
  };

  const removeMaterial = (moduleId: string, lessonId: string, materialId: string) => {
    updateLesson(moduleId, lessonId, (lesson) => ({
      ...lesson,
      materials: lesson.materials.filter((material) => material.id !== materialId)
    }));
  };

  const renderEmptyState = () => (
    <Box
      flex={1}
      minHeight={0}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={3}
      py={8}
      px={4}
      sx={{ overflowY: 'auto' }}
    >
      <Typography sx={{ color: '#FDFDFD', fontSize: 30, fontWeight: 500 }}>Start from scratch.</Typography>
      <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>
        This is your blank canvas. You can create a program that’s fully custom-designed around your own structure,
        titles, and flow.
      </Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600 }}>
        When you’re ready, click "Create Module" to start adding your content.
      </Typography>
      <Button
        startIcon={<AddIcon />}
        sx={{ mt: 2, background: '#1C1C1C', color: '#FDFDFD', borderRadius: 1, px: 3 }}
        onClick={handleAddModule}
      >
        Create Module
      </Button>
    </Box>
  );

  const renderLessonEditor = () => {
    if (!activeModule || !activeLesson) return renderEmptyState();

    const updateLessonField = (field: keyof Lesson, value: any) => {
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({ ...lesson, [field]: value }));
    };

    const handleAddMaterial = () => {
      if (!activeModule || !activeLesson) return;
      setResourceChoiceLesson({ moduleId: activeModule.id, lessonId: activeLesson.id });
    };

    const handleStartLessonEdit = () => {
      setLessonDraft({ title: activeLesson.title, description: activeLesson.description });
      setIsEditingLesson(true);
    };

    const handleCancelLessonEdit = () => {
      setIsEditingLesson(false);
      setLessonDraft({ title: activeLesson.title, description: activeLesson.description });
    };

    const handleSaveLessonEdit = () => {
      if (!activeModule || !activeLesson) return;
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        title: lessonDraft.title,
        description: lessonDraft.description
      }));
      setIsEditingLesson(false);
    };

    const handleAddMultipleChoice = () => {
      const opt1 = { id: nanoid(), label: '' };
      const opt2 = { id: nanoid(), label: '' };
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        multipleChoiceQuestions: [
          ...lesson.multipleChoiceQuestions,
          {
            id: nanoid(),
            prompt: '',
            options: [opt1, opt2],
            correctOptionId: opt1.id
          }
        ]
      }));
    };

    const handleOptionDrop = (mcqId: string, targetId: string | null) => {
      if (!draggingOption) return;
      const draggedOptionId = draggingOption.optionId;
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) => {
          if (item.id !== mcqId) return item;
          const options = [...item.options];
          const fromIndex = options.findIndex((opt) => opt.id === draggedOptionId);
          const toIndex = targetId ? options.findIndex((opt) => opt.id === targetId) : options.length - 1;
          if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return item;
          const [moved] = options.splice(fromIndex, 1);
          options.splice(toIndex, 0, moved);
          let nextCorrect = item.correctOptionId;
          if (nextCorrect && !options.find((opt) => opt.id === nextCorrect)) {
            nextCorrect = options[0]?.id || '';
          }
          return { ...item, options, correctOptionId: nextCorrect };
        })
      }));
      setDraggingOption(null);
    };

    const moduleIndex = modules.findIndex((m) => m.id === activeModule.id) + 1;
    const lessonIndex = activeModule.lessons.findIndex((l) => l.id === activeLesson.id) + 1;
    const lessonPending = Boolean(activeLesson.status && activeLesson.status !== 'completed');
    const aiMaterialPlaceholder = 'https://placehold.co/135x80?text=AI';

    if (lessonPending) {
      return (
        <Box
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Typography sx={{ color: '#B6B6B6' }}>We are still generating this lesson. Please hold on.</Typography>
          <LinearProgress sx={{ width: '60%', borderRadius: 6 }} />
        </Box>
      );
    }

    return (
      <Box flex={1} minHeight={0} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack spacing={4} sx={{ flex: 1, minHeight: 0, p: 3, overflowY: 'auto' }}>
          {isAIMode && infoVisible && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: '#2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#F79420' }} />
              <Box flex={1}>
                <Typography sx={{ color: '#B6B6B6', fontSize: 13 }}>
                  This program takes approximately 2.5 hours to complete in full. Learners may complete it all at once
                  or space it out over time.
                </Typography>
              </Box>
              <Button size="small" onClick={() => setInfoVisible(false)} sx={{ color: '#FDFDFD' }}>
                Dismiss
              </Button>
            </Box>
          )}

          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Level {moduleIndex}</Typography>
            <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 600 }}>
              {activeModule.title || 'Untitled Module'}
            </Typography>
          </Box>

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 700 }}>Lesson Name</Typography>
              {!isEditingLesson && (
                <IconButton size="small" onClick={handleStartLessonEdit}>
                  <EditOutlinedIcon sx={{ color: '#FDFDFD' }} />
                </IconButton>
              )}
            </Box>
            {isEditingLesson ? (
              <DosenInput
                value={lessonDraft.title}
                onChange={(e) => setLessonDraft((draft) => ({ ...draft, title: e.target.value }))}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: 24,
                    fontWeight: 600,
                    color: '#FDFDFD'
                  }
                }}
              />
            ) : (
              <Typography sx={{ color: '#FDFDFD', fontSize: 24, fontWeight: 700 }}>
                {activeLesson.title || 'Untitled Lesson'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 700, mb: 1 }}>Lesson Description</Typography>
            {isEditingLesson ? (
              <DosenInput
                value={lessonDraft.description}
                onChange={(e) => setLessonDraft((draft) => ({ ...draft, description: e.target.value }))}
                multiline
                rows={6}
                fullWidth
              />
            ) : (
              <>
                {activeLesson.description?.trim() ? (
                  <Box
                    sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 400, lineHeight: '21px' }}
                    dangerouslySetInnerHTML={{
                      __html: activeLesson.description.includes('<')
                        ? activeLesson.description
                        : activeLesson.description.replace(/\n/g, '<br />')
                    }}
                  />
                ) : (
                  <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 400, lineHeight: '21px' }}>
                    No description yet.
                  </Typography>
                )}
              </>
            )}
          </Box>

          {isEditingLesson && (
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancelLessonEdit}
                sx={{ color: '#FDFDFD', borderColor: '#868686' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveLessonEdit}
                sx={{ background: '#F79420', color: '#0F0F0F' }}
              >
                Save
              </Button>
            </Box>
          )}

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 700 }}>Lesson Materials</Typography>
              <Box display="flex" gap={1}>
                <Tooltip
                  title="Save your changes before adding a new material."
                  disableHoverListener={!shouldDisablePendingActions}
                  arrow
                >
                  <span style={{ display: 'inline-flex' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddMaterial}
                      disabled={shouldDisablePendingActions || saving}
                    >
                      Add Material
                      {/* {state.groupId} */}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box display="inline-flex" flexWrap="wrap" alignItems="flex-start" gap={3}>
              {activeLesson.materials.map((material) => {
                const materialSource = material.source || 'custom';
                const showCard = materialSource === 'library' || materialSource === 'ai';
                const isArticle = materialSource === 'library';
                const isAI = materialSource === 'ai';
                return (
                  <Box
                    key={material.id}
                    onClick={() => {
                      if (showCard) {
                        openMaterialEditor(material);
                      }
                    }}
                    sx={{
                      width: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      position: 'relative',
                      cursor: isArticle ? 'pointer' : 'default'
                    }}
                  >
                    {showCard ? (
                      <>
                        <Box
                          component="img"
                          src={material.thumbnail || (isAI ? aiMaterialPlaceholder : 'https://placehold.co/135x80')}
                          alt={material.title || 'Lesson material'}
                          sx={{
                            width: '100%',
                            height: 90,
                            borderRadius: 1,
                            outline: '2px solid #FDFDFD',
                            objectFit: 'cover'
                          }}
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ color: '#FDFDFD', fontSize: 12 }} noWrap>
                            {material.title || (isAI ? 'AI generated material' : 'Resource')}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeMaterial(activeModule.id, activeLesson.id, material.id);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" style={{ color: 'white', cursor: 'pointer' }} />
                          </IconButton>
                        </Box>
                        {isAI ? (
                          <Typography sx={{ color: '#868686', fontSize: 10 }}>Generated by AI</Typography>
                        ) : (
                          material.readTime && (
                            <Typography sx={{ color: '#868686', fontSize: 10 }}>{material.readTime}</Typography>
                          )
                        )}
                        {material.reference && (
                          <Button
                            size="small"
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', color: '#FDFDFD' }}
                            onClick={() => window.open(material.reference, '_blank')}
                          >
                            Open resource
                          </Button>
                        )}
                      </>
                    ) : (
                      <Stack spacing={1}>
                        <DosenInput
                          label="Material title"
                          value={material.title}
                          onChange={(e) =>
                            updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                              ...lesson,
                              materials: lesson.materials.map((item) =>
                                item.id === material.id ? { ...item, title: e.target.value } : item
                              )
                            }))
                          }
                        />
                        <DosenInput
                          label="Link or reference"
                          value={material.reference}
                          onChange={(e) =>
                            updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                              ...lesson,
                              materials: lesson.materials.map((item) =>
                                item.id === material.id ? { ...item, reference: e.target.value } : item
                              )
                            }))
                          }
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeMaterial(activeModule.id, activeLesson.id, material.id)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    )}
                  </Box>
                );
              })}

              {/* <Box
                role="button"
                tabIndex={0}
                onClick={() => setMaterialsModalLesson({ moduleId: activeModule.id, lessonId: activeLesson.id })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setMaterialsModalLesson({ moduleId: activeModule.id, lessonId: activeLesson.id });
                  }
                }}
                sx={{
                  width: 150,
                  height: 110,
                  borderRadius: 1,
                  border: '2px dashed #2A2A2A',
                  color: '#B6B6B6',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: '#FDFDFD'
                  }}
                />
                <Typography sx={{ fontSize: 12 }}>Add new material</Typography>
              </Box> */}
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#2A2A2A' }} />

          <GenericAccordion
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={12} fontWeight={700} color="white">
                  Feedback Questions
                </Typography>
                <>
                  <Button
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddFeedbackQuestion();
                    }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: 'Inter, sans-serif',
                      color: '#F79420',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        background: 'rgba(247, 148, 32, 0.08)'
                      }
                    }}
                  >
                    Add New
                    <AddIcon sx={{ fontSize: 20, color: '#F79420' }} />
                  </Button>
                </>
              </Box>
            }
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 2.5 }}>
              {activeLesson.feedbackQuestions.length === 0 && (
                <Typography sx={{ color: '#868686', fontSize: 13, px: 1 }}>No feedback questions yet.</Typography>
              )}
              {activeLesson.feedbackQuestions.map((question) => (
                <Box
                  key={question.id}
                  sx={{
                    p: 2.5,
                    borderRadius: 1.5,
                    background: '#1C1C1C',
                    border: '1px solid #2A2A2A',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '24px' }}>
                    {question.prompt || 'Untitled question'}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: '#868686', fontSize: 12 }}>
                      {getFeedbackTypeLabel(question.type)}
                    </Typography>
                    <Button
                      color="error"
                      onClick={() => setRemoveFeedbackId(question.id)}
                      sx={{ textDecoration: 'underline', fontSize: 12, fontWeight: 600 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </GenericAccordion>

          <GenericAccordion
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={12} fontWeight={700} color="white">
                  Multiple Choice Questions
                </Typography>
                <>
                  <Button
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddMultipleChoice();
                    }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: 'Inter, sans-serif',
                      color: '#F79420',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        background: 'rgba(247, 148, 32, 0.08)'
                      }
                    }}
                  >
                    Add New
                    <AddIcon sx={{ fontSize: 20, color: '#F79420' }} />
                  </Button>
                </>
              </Box>
            }
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px,1fr))', gap: 2 }}>
              {activeLesson.multipleChoiceQuestions.length === 0 && (
                <Typography sx={{ color: '#868686', fontSize: 13, px: 1 }}>
                  No multiple choice questions yet.
                </Typography>
              )}
              {activeLesson.multipleChoiceQuestions.map((mcq) => {
                const selectedOptionId = mcq.correctOptionId || mcq.options[0]?.id || '';
                return (
                  <Box
                    key={mcq.id}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      background: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIndicatorIcon sx={{ color: '#868686' }} />
                      <DosenInput
                        label="Question"
                        value={mcq.prompt}
                        onChange={(e) =>
                          updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                            ...lesson,
                            multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                              item.id === mcq.id ? { ...item, prompt: e.target.value } : item
                            )
                          }))
                        }
                        fullWidth
                      />
                    </Box>
                    <Stack spacing={1}>
                      {mcq.options.map((option) => (
                        <Box
                          key={option.id}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          draggable
                          onDragStart={() => setDraggingOption({ mcqId: mcq.id, optionId: option.id })}
                          onDragOver={(e) => draggingOption && e.preventDefault()}
                          onDrop={() => handleOptionDrop(mcq.id, option.id)}
                          sx={{ cursor: 'grab' }}
                        >
                          <DragIndicatorIcon sx={{ color: '#868686', fontSize: 18 }} />
                          <Radio
                            checked={selectedOptionId === option.id}
                            onChange={() =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                                  item.id === mcq.id ? { ...item, correctOptionId: option.id } : item
                                )
                              }))
                            }
                            sx={{
                              color: '#868686',
                              '&.Mui-checked': { color: '#FDFDFD' }
                            }}
                          />
                          <DosenInput
                            label="Option"
                            value={option.label}
                            onChange={(e) =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                                  item.id === mcq.id
                                    ? {
                                        ...item,
                                        options: item.options.map((opt) =>
                                          opt.id === option.id ? { ...opt, label: e.target.value } : opt
                                        )
                                      }
                                    : item
                                )
                              }))
                            }
                            fullWidth
                          />
                          <IconButton
                            onClick={() =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) => {
                                  if (item.id !== mcq.id) return item;
                                  const filtered = item.options.filter((opt) => opt.id !== option.id);
                                  const newCorrect =
                                    item.correctOptionId === option.id ? filtered[0]?.id || '' : item.correctOptionId;
                                  return { ...item, options: filtered, correctOptionId: newCorrect };
                                })
                              }))
                            }
                            sx={{ color: '#FDFDFD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <Box sx={{ width: 16, height: 2, backgroundColor: '#FDFDFD' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                            ...lesson,
                            multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                              item.id === mcq.id
                                ? { ...item, options: [...item.options, { id: nanoid(), label: '' }] }
                                : item
                            )
                          }))
                        }
                        sx={{ color: '#FDFDFD' }}
                      >
                        <AddIcon />
                      </IconButton>
                      <Box
                        sx={{
                          color: '#FF0000',
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'underline',
                          minWidth: 0,
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px',
                          cursor: 'pointer'
                        }}
                        onClick={() => setRemoveMcqId(mcq.id)}
                      >
                        Remove
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </GenericAccordion>
        </Stack>
      </Box>
    );
  };

  const renderModuleList = () => (
    <Box
      sx={{
        width: 320,
        borderRight: '1px solid #2A2A2A',
        background: '#151515',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden'
      }}
      onDragOver={(e) => draggingModule && e.preventDefault()}
      onDrop={() => handleModuleDragDrop(null)}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 600, flex: 1 }}>Modules</Typography>
        <IconButton size="small" onClick={handleAddModule}>
          <AddIcon sx={{ color: '#FDFDFD' }} />
        </IconButton>
      </Box>
      {modules.length === 0 ? (
        <Box px={3} py={6} display="flex" flexDirection="column" gap={2}>
          <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600 }}>
            You have not created a module yet.
          </Typography>
          <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>Create your first module to continue.</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddModule}
            sx={{ background: '#1C1C1C', color: '#FDFDFD', width: 'fit-content' }}
          >
            Create Module
          </Button>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {modules.map((module) => {
            const moduleIndex = modules.findIndex((m) => m.id === module.id);
            const isActiveModule = module.id === selectedModuleId;
            const moduleProcessing = Boolean(module.status && module.status !== 'completed');
            console.log('[ManualProgramBuilder] module row', {
              moduleId: module.id,
              status: module.status,
              moduleProcessing,
              selectedModuleId
            });
            return (
              <Box key={module.id}>
                <Box
                  draggable={!moduleProcessing}
                  onDragStart={() => !moduleProcessing && setDraggingModule(module.id)}
                  onDragOver={(e) => !moduleProcessing && e.preventDefault()}
                  onDrop={() => !moduleProcessing && handleModuleDragDrop(module.id)}
                  onDragEnd={() => setDraggingModule(null)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    cursor: moduleProcessing ? 'not-allowed' : 'grab',
                    bgcolor: isActiveModule ? '#1C1C1C' : 'transparent',
                    opacity: moduleProcessing ? 0.6 : 1
                  }}
                  onClick={() => {
                    if (moduleProcessing) return;
                    setSelectedModuleId(module.id);
                    setSelectedLessonId(module.lessons[0]?.id || null);
                  }}
                >
                  <DragIndicatorIcon sx={{ color: '#868686' }} />
                  <Box flex={1} sx={{ pr: 1 }}>
                    <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{`Module ${moduleIndex + 1}`}</Typography>
                    {editingModuleId === module.id ? (
                      <DosenInput
                        value={module.title}
                        onClick={(e: any) => e.stopPropagation()}
                        onChange={(e) =>
                          updateModule(module.id, (m) => ({
                            ...m,
                            title: e.target.value
                          }))
                        }
                        onBlur={() => setEditingModuleId(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setEditingModuleId(null);
                          } else if (e.key === 'Escape') {
                            setEditingModuleId(null);
                          }
                        }}
                        // sx={{ ...inputSx, mt: 0.5 }}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        {moduleProcessing ? (
                          <Skeleton variant="text" width={120} sx={{ bgcolor: '#333' }} />
                        ) : (
                          <Typography
                            sx={{ color: isActiveModule ? '#F79420' : '#FDFDFD', fontSize: 16, fontWeight: 600 }}
                          >
                            {module.title || 'Untitled Module'}
                          </Typography>
                        )}
                        {moduleProcessing && <CircularProgress size={12} sx={{ color: '#F79420' }} />}
                      </Box>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    disabled={moduleProcessing}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (moduleProcessing) return;
                      handleAddLesson(module.id);
                    }}
                  >
                    <AddIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => openModuleMenu(e, module.id)}>
                    <MoreVertIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                  </IconButton>
                </Box>
                <Divider sx={{ borderColor: '#2A2A2A' }} />
                {module.lessons.map((lesson) => {
                  const lessonIndex = module.lessons.findIndex((l) => l.id === lesson.id);
                  const isActiveLesson = isActiveModule && lesson.id === selectedLessonId;
                  const lessonProcessing = Boolean(lesson.status && lesson.status !== 'completed');
                  console.log('[ManualProgramBuilder] lesson row', {
                    lessonId: lesson.id,
                    status: lesson.status,
                    lessonProcessing,
                    selectedLessonId
                  });
                  const isRenamingLesson = lessonRenameMeta?.lessonId === lesson.id;
                  const renameValue = lessonRenameMeta?.title ?? lesson.title;
                  return (
                    <Box
                      key={lesson.id}
                      draggable={!lessonProcessing}
                      onDragStart={() =>
                        !lessonProcessing && setDraggingLesson({ moduleId: module.id, lessonId: lesson.id })
                      }
                      onDragOver={(e) => !lessonProcessing && e.preventDefault()}
                      onDrop={() => !lessonProcessing && handleLessonDrop(module.id, lesson.id)}
                      onDragEnd={() => setDraggingLesson(null)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        cursor: lessonProcessing ? 'not-allowed' : 'grab',
                        bgcolor: isActiveLesson ? '#1C1C1C' : 'transparent',
                        opacity: lessonProcessing ? 0.6 : 1
                      }}
                      onClick={() => {
                        if (lessonProcessing) return;
                        setSelectedModuleId(module.id);
                        setSelectedLessonId(lesson.id);
                      }}
                      onDoubleClick={(event) => {
                        if (lessonProcessing) return;
                        event.stopPropagation();
                        beginRenameLesson(module.id, lesson.id, lesson.title || '');
                      }}
                    >
                      <Box width={20} textAlign="center">
                        <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>
                          {lessonIndex + 1}
                        </Typography>
                      </Box>
                      <Box flex={1}>
                        {isRenamingLesson ? (
                          <DosenInput
                            value={renameValue}
                            onChange={(e) =>
                              setLessonRenameMeta((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      title: e.target.value
                                    }
                                  : prev
                              )
                            }
                            onBlur={finishLessonRename}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                finishLessonRename();
                              }
                              if (e.key === 'Escape') {
                                cancelLessonRename();
                              }
                            }}
                            onClick={(event) => event.stopPropagation()}
                            fullWidth
                            autoFocus
                          />
                        ) : lessonProcessing ? (
                          <Skeleton variant="text" width="80%" sx={{ bgcolor: '#333' }} />
                        ) : (
                          <Typography
                            sx={{ color: isActiveLesson ? '#F79420' : '#FDFDFD', fontSize: 14, fontWeight: 600 }}
                          >
                            {lesson.title}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openLessonMenu(e, { moduleId: module.id, lessonId: lesson.id });
                        }}
                      >
                        <MoreVertIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                      </IconButton>
                    </Box>
                  );
                })}
                <Button
                  sx={{ color: '#868686', fontSize: 12, justifyContent: 'flex-start', pl: 3 }}
                  onClick={() => handleAddLesson(module.id)}
                  startIcon={<AddIcon fontSize="small" />}
                >
                  Add Lesson
                </Button>
                <Divider sx={{ borderColor: '#2A2A2A' }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          minHeight: 0,
          p: 3,
          bgcolor: '#0F0F0F',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 2,
            border: '1px solid #2A2A2A',
            bgcolor: '#1C1C1C',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <Box
            sx={{ px: 3, py: 2.5, borderBottom: '1px solid #2A2A2A', display: 'flex', alignItems: 'center', gap: 3 }}
          >
            <DosenInput
              label="Program name"
              value={programName}
              onChange={(e) => handleProgramNameChange(e.target.value)}
              inputSx={{ width: 320 }}
            />
            {isAIMode && isContentGenerating ? (
              <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Generating AI content…</Typography> */}
                <LinearProgress
                  variant={contentProgressValue ? 'determinate' : 'indeterminate'}
                  value={contentProgressValue ?? undefined}
                />
              </Box>
            ) : (
              <Box flex={1} />
            )}
            {!hideSaveButton && showLessonEditor && (
              <Box display="flex" justifyContent="flex-end">
                <DosenButton
                  // variant="contained"
                  // startIcon={}
                  disabled={!dirty || saving || (isAIMode && isContentGenerating)}
                  onClick={handleSave}
                  sx={{
                    background: dirty ? 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)' : 'rgba(0,0,0,0.2)',
                    color: dirty ? '#FDFDFD' : '#565656',
                    width: '200px'
                    // borderRadius: 1,
                    // px: 3
                  }}
                >
                  <SaveIcon />
                  Save Changes
                </DosenButton>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden', height: '100%' }}>
            {renderModuleList()}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                background: '#151515',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              {showLessonEditor ? (
                renderLessonEditor()
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    px: '25px',
                    py: '100px',
                    overflow: 'hidden',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    fontFamily: 'Open Sans !important'
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: '30px',
                      fontFamily: 'Open Sans !important'
                    }}
                  >
                    {/* Título */}
                    <Typography
                      sx={{
                        alignSelf: 'stretch',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        fontSize: '30px',
                        fontFamily: 'Open Sans !important',
                        fontWeight: 500,
                        wordWrap: 'break-word'
                      }}
                    >
                      Review your program structure.
                    </Typography>

                    {/* Texto descritivo */}
                    <Typography
                      sx={{
                        color: 'var(--Text-text-secondary, #B6B6B6)',
                        fontSize: '14px',
                        fontFamily: 'Open Sans !important',
                        fontWeight: 400,
                        lineHeight: '19.88px',
                        whiteSpace: 'pre-line',
                        wordWrap: 'break-word'
                      }}
                    >
                      {`Over to the left, you’ll see our recommended Module and Lesson titles.
Before we go ahead and design the content for each one, take a moment to review these titles.

You can edit, reorder, delete, and add until you are satisfied with each.`}
                    </Typography>

                    {/* Texto final */}
                    <Typography
                      sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        fontSize: '16px',
                        fontFamily: 'Open Sans !important',
                        fontWeight: 600,
                        lineHeight: '20.8px',
                        wordWrap: 'break-word'
                      }}
                    >
                      Once you’re happy with these, click on “Next” to review your contents.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Menu anchorEl={moduleMenuAnchor} open={Boolean(moduleMenuAnchor)} onClose={closeModuleMenu}>
          <MenuItem
            onClick={() => {
              if (moduleMenuId) beginRenameModule(moduleMenuId);
            }}
          >
            Rename module
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (moduleMenuId) removeModule(moduleMenuId);
              closeModuleMenu();
            }}
          >
            Delete module
          </MenuItem>
        </Menu>

        {(() => {
          const selectedLesson =
            lessonMenuMeta &&
            modules
              .find((module) => module.id === lessonMenuMeta.moduleId)
              ?.lessons.find((lesson) => lesson.id === lessonMenuMeta.lessonId);
          const isLessonLocked = Boolean(selectedLesson?.status && selectedLesson.status !== 'completed');

          return (
            <Menu anchorEl={lessonMenuAnchor} open={Boolean(lessonMenuAnchor)} onClose={closeLessonMenu}>
              <MenuItem
                onClick={() => {
                  if (lessonMenuMeta) {
                    const currentModule = modules.find((module) => module.id === lessonMenuMeta.moduleId);
                    const currentLesson = currentModule?.lessons.find(
                      (lesson) => lesson.id === lessonMenuMeta.lessonId
                    );
                    beginRenameLesson(lessonMenuMeta.moduleId, lessonMenuMeta.lessonId, currentLesson?.title || '');
                  }
                }}
              >
                Rename lesson
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (lessonMenuMeta) removeLesson(lessonMenuMeta.moduleId, lessonMenuMeta.lessonId);
                  closeLessonMenu();
                }}
                disabled={isLessonLocked}
              >
                Delete lesson
              </MenuItem>
            </Menu>
          );
        })()}

        {showManualFooter && (
          <Box
            sx={{
              mt: 2,
              borderTop: '1px solid #2A2A2A',
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('../step-2')}
              sx={{ color: '#FDFDFD', borderColor: '#FDFDFD' }}
            >
              Back
            </Button>
            <Box display="flex" alignItems="center" gap={2}>
              {!state.groupId && (
                <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Save your content to unlock Members.</Typography>
              )}
              <Tooltip title="Save your changes before continuing." disableHoverListener={!isNextButtonDisabled} arrow>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('members')}
                    disabled={isNextButtonDisabled}
                    sx={{ bgcolor: '#F79420', color: '#000', '&:hover': { bgcolor: '#ff9f2d' } }}
                  >
                    Next
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>

      <ContentsModal
        openContents={Boolean(materialsModalLesson)}
        onClose={() => setMaterialsModalLesson(null)}
        setArticles={handleMaterialSelection}
        articlesSelected={currentMaterialsSelection}
      />
      <Dialog
        open={Boolean(resourceChoiceLesson)}
        onClose={closeResourceChoice}
        fullWidth
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: '90%',
            height: 254,
            bgcolor: 'rgba(67, 67, 76, 0.20)',
            borderRadius: 2
          }
        }}
      >
        <CommonModalHeader title="Add new resource" handleCloseModal={closeResourceChoice} />
        <DialogContent sx={{ pt: 3, pb: 4 }}>
          <Typography
            align="center"
            color="#FDFDFD"
            fontSize={16}
            fontFamily="Open Sans, sans-serif"
            fontWeight={400}
            lineHeight="24px"
            sx={{ mb: 4 }}
          >
            How you want to add new resource?
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              onClick={handleSelectExistingResource}
              sx={{
                width: 232,
                height: 49,
                bgcolor: '#FFFFFF',
                color: '#000',
                borderRadius: '10px',
                border: '1px solid #EFF0F4',
                textTransform: 'none',
                fontFamily: 'Open Sans, sans-serif',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#f7f7f7',
                  boxShadow: 2
                }
              }}
            >
              Select existing resource
            </Button>
            <Button
              onClick={handleCreateNewResource}
              sx={{
                width: 232,
                height: 49,
                bgcolor: '#FFFFFF',
                color: '#000',
                borderRadius: '10px',
                border: '1px solid #EFF0F4',
                textTransform: 'none',
                fontFamily: 'Open Sans, sans-serif',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#f7f7f7',
                  boxShadow: 2
                }
              }}
            >
              Create new
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addFeedbackModalOpen}
        onClose={() => setAddFeedbackModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
            Add Feedback Question
          </Typography>
          <IconButton onClick={() => setAddFeedbackModalOpen(false)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          <DosenInput
            label="Question*"
            value={feedbackDraft.prompt}
            onChange={(e: any) => setFeedbackDraft((draft) => ({ ...draft, prompt: e.target.value }))}
            placeholder="Type here"
          />
          <Box>
            <InputLabel sx={{ color: '#FDFDFD', mb: 0.5 }}>Answer Type*</InputLabel>
            <DosenAutocomplete
              options={feedbackAnswerOptions}
              getOptionLabel={(opt: any) => opt.label}
              value={feedbackAnswerOptions.find((opt) => opt.type === feedbackDraft.type) || null}
              onChange={(_: any, opt: any) =>
                setFeedbackDraft((draft) => ({
                  ...draft,
                  type: opt?.type || 'text'
                }))
              }
              // renderInput={(params: any) => (
              //   <TextField
              //     {...params}
              //     fullWidth
              //     placeholder="Answer Format"
              //     sx={{
              //       '& .MuiInputBase-root': {
              //         background: 'rgba(0,0,0,0.70)',
              //         color: '#FDFDFD',
              //         borderRadius: '6px',
              //         outline: '1px solid #868686',
              //         '&:hover': { outlineColor: '#B4B4B4' },
              //         '&.Mui-focused': { outlineColor: '#F79420' },
              //         '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
              //       },
              //       '& input': {
              //         color: '#FDFDFD'
              //       }
              //     }}
              //   />
              // )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setAddFeedbackModalOpen(false)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton onClick={handleSaveNewFeedback} sx={{ flex: 1 }} disabled={!feedbackDraft.prompt.trim()}>
            Add Question
          </DosenButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removeFeedbackId)}
        onClose={() => setRemoveFeedbackId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>Remove Question</Typography>
          <IconButton onClick={() => setRemoveFeedbackId(null)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>
            Are you sure to remove this question?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setRemoveFeedbackId(null)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton onClick={handleConfirmRemoveFeedback} sx={{ flex: 1 }}>
            Remove
          </DosenButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removeMcqId)}
        onClose={() => setRemoveMcqId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>Remove Assessment</Typography>
          <IconButton onClick={() => setRemoveMcqId(null)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>
            Are you sure to remove this assessment?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setRemoveMcqId(null)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton
            onClick={() => {
              if (!removeMcqId || !activeModule || !activeLesson) return;
              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                ...lesson,
                multipleChoiceQuestions: lesson.multipleChoiceQuestions.filter((item) => item.id !== removeMcqId)
              }));
              setRemoveMcqId(null);
            }}
            sx={{ flex: 1 }}
          >
            Remove
          </DosenButton>
        </DialogActions>
      </Dialog>
      <AddEditContent
        openForm={addEditModalMeta.open}
        handleClickCloseForm={handleCloseAddEditModal}
        setOpenForm={(value: boolean) => setAddEditModalMeta((prev) => ({ ...prev, open: value }))}
        selectedActiveId={addEditModalMeta.articleId}
        singleListData={
          addEditModalMeta.articleData ||
          addEditModalMeta.material ||
          (addEditModalMeta.articleId ? articleMap[addEditModalMeta.articleId] : undefined)
        }
        action={addEditModalMeta.action}
        setAction={undefined}
        onNewArticleCreated={handleNewArticleCreated}
        newGroupId={state.groupId ?? ''}
      />
    </>
  );
};
